import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  FC,
  ChangeEvent,
} from 'react';

import PropTypes from 'prop-types';

import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale, safeSearchRegex } from '@adsk/alloy-react-helpers';
import { MagnifyingGlassIcon, XIcon } from '@adsk/alloy-react-icon';
import TextInput from '@adsk/alloy-react-text-input';
import theme from '@adsk/alloy-react-theme';

import { ANALYTICS_TYPES, ANALYTICS_DEBOUNCE } from '../helpers/analytics';
import { accountsListShape, projectsListShape } from '../helpers/propTypes';
import { Account, Project } from '../helpers/types';
import { useDebounce } from '../hooks/useDebounce';
import ProjectRowLoader from './ProjectRowLoader';
import ProjectsError from './ProjectsError';
import VirtualizedProjectList from './VirtualizedProjectList';

type ProjectPickerProps = {
  accounts?: Account[];
  currentAccountId?: string;
  projects?: Project[];
  currentProjectId?: string;
  templates?: Project[];
  currentProductId?: string;
  projectsLoading?: boolean;
  projectsError?: boolean;
  projectsRetry?: () => void;
  onClose?: () => void;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
};

const ProjectPicker: FC<ProjectPickerProps> = ({
  accounts,
  currentAccountId,
  projects,
  currentProjectId,
  templates,
  currentProductId,
  projectsLoading,
  projectsError,
  projectsRetry,
  onClose,
  onAccountClick,
  onProjectClick,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState<string>('');

  const reportSearchOnChangeAnalyticFn = useMemo(
    () => (debounceSearch: string) => {
      if (debounceSearch && debounceSearch !== '') {
        analytics.track(
          ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_SEARCH_CHANGED,
          {
            product_id: currentProductId,
            searched_text: debounceSearch,
          }
        );
      }
    },
    [currentProductId]
  );

  useDebounce(search, ANALYTICS_DEBOUNCE, reportSearchOnChangeAnalyticFn);

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleAnalyticOnClick = (projectParam: object | undefined) => {
    const project = projectParam as Project;
    const data = {
      product_ids:
        project.products &&
        project.products.map((product) => product.key).join(', '),
      product_names:
        project.products &&
        project.products.map((product) => product.name).join(', '),
      project_id: project.id,
      project_name: project.name,
    };
    analytics.track(
      project.classification === 'template'
        ? ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_TEMPLATE_CLICK
        : ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PROJECT_CLICK,
      data
    );
  };

  const projectsSearch = useMemo(
    () =>
      (projects &&
        projects.filter((p) => safeSearchRegex(search).test(p.name))) ||
      [],
    [projects, search]
  );

  const accountsSearch = useMemo(
    () =>
      (accounts &&
        accounts.filter((a) =>
          safeSearchRegex(search).test(a.account_display_name)
        )) ||
      [],
    [accounts, search]
  );

  const templatesSearch = useMemo(
    () =>
      (templates &&
        templates.filter((t) => safeSearchRegex(search).test(t.name))) ||
      [],
    [templates, search]
  );

  const searchResults =
    projectsSearch.length + accountsSearch.length + templatesSearch.length;

  useEffect(() => searchRef.current?.focus(), []);

  return (
    <div css={[{ width: 360 }]}>
      <div
        css={[
          {
            position: 'relative',
            padding: '10px 16px 0',
          },
        ]}
      >
        <TextInput
          data-testid="ProjectPicker_input"
          renderLeftIcon={(p) => <MagnifyingGlassIcon {...p} />}
          placeholder={T.translate('SEARCH')?.toString()}
          value={search || ''}
          onChange={onSearchChange}
          ref={searchRef}
        />
        {search && (
          <XIcon
            size={20}
            style={{
              position: 'absolute',
              top: 19,
              right: 25,
              color: theme.colors.charcoal500,
              cursor: 'pointer',
            }}
            onClick={() => setSearch('')}
          />
        )}
      </div>

      {search && !!searchResults && (
        <div
          css={[
            theme.typography.heading5,
            { color: theme.colors.charcoal700, padding: '8px 16px' },
          ]}
        >
          {T.translate('RESULTS_FOUND', { context: searchResults })}
        </div>
      )}

      {search && !searchResults && !projectsLoading && !projectsError && (
        <div
          css={[
            theme.typography.bodySmall,
            {
              color: theme.colors.charcoal700,
              textAlign: 'center',
              padding: '36px 0',
            },
          ]}
        >
          {T.translate('NO_RESULTS_FOUND')}
        </div>
      )}

      {search && !searchResults && projectsLoading && <ProjectRowLoader />}

      {search && !searchResults && projectsError && !projectsLoading && (
        <ProjectsError
          retry={projectsRetry}
          projectsCount={projectsSearch.length}
        />
      )}

      {!!searchResults && (
        <VirtualizedProjectList
          projects={projectsSearch}
          accounts={accountsSearch}
          templates={templatesSearch}
          currentAccountId={currentAccountId}
          currentProjectId={currentProjectId}
          currentProductId={currentProductId}
          projectsLoading={projectsLoading}
          projectsError={projectsError}
          projectsRetry={projectsRetry}
          onClose={onClose}
          onAccountClick={onAccountClick}
          onProjectClick={onProjectClick}
          handleAnalyticOnClick={handleAnalyticOnClick}
        />
      )}
      <div
        css={[
          {
            borderTop: `1px solid ${theme.colors.charcoal200}`,
            padding: '14px 12px',
          },
        ]}
      >
        <LinkButton
          as="a"
          size={LinkButton.SIZES.SMALL}
          style={theme.typography.bodyMedium}
          href={`${getAccHostname()}/projects`}
          onClick={() => {
            analytics.track(
              ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_ALLPROJECTS_CLICK,
              {
                product_id: currentProjectId,
              }
            );
          }}
        >
          {T.translate('VIEW_ALL_PROJECTS')}
        </LinkButton>
      </div>
    </div>
  );
};

ProjectPicker.displayName = 'ProjectPicker';

ProjectPicker.propTypes = {
  /** Available accounts to select from */
  accounts: accountsListShape,
  /** current selected account id */
  currentAccountId: PropTypes.string,
  /** Available projects to select from */
  projects: projectsListShape,
  /** Available templates */
  templates: projectsListShape,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** current product ID, to check if project has entitlements for or not */
  currentProductId: PropTypes.string,
  /** show projects loader */
  projectsLoading: PropTypes.bool,
  /** is project entitlements ended in error */
  projectsError: PropTypes.bool,
  /** retry loading project entitlements */
  projectsRetry: PropTypes.func,
  /** menu close handler */
  onClose: PropTypes.func,
  /** account click handler */
  onAccountClick: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func,
};

export default ProjectPicker;
